import {useCallback, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import toast from 'react-hot-toast'
import {CardElement, useStripe, useElements} from '@stripe/react-stripe-js'
import type {StripeCardElement} from '@stripe/stripe-js'
// Helpers
import {checkOutWithStripe, CheckOutWithStripeRequest} from '../../lib/api'
import {logDataByLevel} from '../../utils/logging-helpers'
import {deepTrim, truncateString} from '../../utils/string-helpers'
import {handlePaymentAttempt, shouldAcceptPaymentClick} from './checkout-payment-flow'
import type {PaymentResult} from './checkout-payment-flow'
import {CHECKOUT_TIMEOUT_MS} from '../../consts/payments'
// Types
import type {StripePayload, SubmissionObject} from '../../types/submission-wizard'
// Styles
import styles from './checkout-form.module.scss'

interface CheckoutFormProps {
  stripePayload: StripePayload
  paymentIsProcessing: boolean
  sendPaymentIsProcessing: (isProcessing: boolean) => void
  onCastSubmission: (submission: SubmissionObject) => Promise<boolean>
  onNext: () => void
}

const CheckoutForm = ({
  stripePayload,
  paymentIsProcessing,
  sendPaymentIsProcessing,
  onCastSubmission,
  onNext,
}: CheckoutFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isDisabled, setIsDisabled] = useState(false)

  const [paymentButtonText, setPaymentButtonText] = useState(() => {
    const formattedPaymentAmount = (stripePayload.paymentAmount / 100).toLocaleString('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2})
    return `Pay ${formattedPaymentAmount} USD`
  })

  const [ref, setRef] = useState<StripeCardElement | null>(null)
  // Re-entrancy + debounce guards live in refs so a rapid second click is
  // rejected synchronously (before any state update / re-render can race).
  const inFlightRef = useRef(false)
  const lastAcceptedTimestampRef = useRef<number | null>(null)

  // Stripe.js, Elements, AND the mounted CardElement (ref, set on its onReady)
  // must all be available before a payment can be created; gate the button on
  // this so a click can never hit a null stripe/elements or an unmounted card.
  const stripeReady = Boolean(stripe && elements && ref)

  useEffect(() => {
    const formattedPaymentAmount = (stripePayload.paymentAmount / 100).toLocaleString('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2})
    const newPaymentButtonText = (
      paymentIsProcessing
        ? 'Processing...'
        : `Pay ${formattedPaymentAmount} USD`
    )
    setIsDisabled(paymentIsProcessing)
    setPaymentButtonText(newPaymentButtonText)
  }, [paymentIsProcessing, stripePayload.paymentAmount])

  const buildCheckoutBodyWithStripePaymentMethodId = useCallback(
    (stripePaymentMethodId: string | undefined): CheckOutWithStripeRequest | null => {
      // Normalize user-entered values first so whitespace-only or invisible-only
      // input is treated as empty and trailing spaces don't consume the
      // truncation budget. deepTrim strips zero-width/invisible chars then trims.
      const submissionName = deepTrim(String(stripePayload.submissionName ?? ''))
      const submissionUrl = deepTrim(String(stripePayload.submissionUrl ?? ''))
      const voterEmail = deepTrim(String(stripePayload.voterEmail ?? ''))
      const voterName = deepTrim(String(stripePayload.voterName ?? ''))

      // voterEmail is required by the backend, so the request is never built
      // without it (belt-and-suspenders alongside the guard in createStripePayment).
      if (
        _.isEmpty(stripePayload)
        || submissionUrl.length === 0
        || submissionName.length === 0
        || voterEmail.length === 0
        || !stripePaymentMethodId
      ) {
        return null
      }

      let description = `Name of contest:  ${stripePayload.ballotName}\n`
      description += `Photo Name:  ${submissionName}\n`
      description += `Photo Url:  ${submissionUrl}\n`
      description += `Category:  ${stripePayload.questionName}`

      const metadata = deepTrim({
        email: truncateString(voterEmail, 450),
        name: truncateString(voterName, 450),
        submission_name: truncateString(submissionName, 450),
        submission_photo_url: truncateString(submissionUrl, 450),
        ballot_id: stripePayload.ballotId,
        ballot_name: truncateString(stripePayload.ballotName, 450),
        group_slug: truncateString(stripePayload.groupSlug, 450),
        category_slug: truncateString(stripePayload.questionSlug, 450),
        category_name: truncateString(stripePayload.questionName, 450),
      })

      const checkoutBody: CheckOutWithStripeRequest = {
        amount: stripePayload.paymentAmount,
        id: stripePaymentMethodId,
        customer_id: stripePayload.customerId,
        description: description,
        email: voterEmail,
        metadata: JSON.stringify(metadata),
        photo_submission: true,
      }
      return checkoutBody
    }, [stripePayload])

  // Performs tokenization + backend checkout. Resolves {success,message} for
  // "soft" outcomes (declined card, incomplete payload); THROWS for hard errors
  // (backend 4xx/5xx, network, timeout) so handlePaymentAttempt catches them and
  // guarantees the spinner is reset. No non-null assertions: the caller only
  // runs this once stripeReady is true, and we re-check defensively.
  const createStripePayment = async (signal: AbortSignal): Promise<PaymentResult> => {
    if (!stripe || !elements) throw new Error('Stripe is not ready')
    const cardElement = elements.getElement(CardElement)
    if (!cardElement) throw new Error('Card element is not mounted')

    // Email is required by the backend and must never be sent empty. A voter is
    // always signed in at this step, so an empty email means their identity
    // wasn't loaded into the payload — surface it instead of tokenizing a card
    // and hitting the backend with a payload it will reject.
    const voterEmail = deepTrim(String(stripePayload.voterEmail ?? ''))
    if (!voterEmail) {
      logDataByLevel({
        level: 'error',
        message: 'PURCHASE-F_NO_EMAIL: attempted checkout with an empty voter email',
        data: {ballotId: stripePayload.ballotId, questionSlug: stripePayload.questionSlug},
      })
      return {
        success: false,
        message: 'We couldn’t read your account email. Please refresh the page (and sign in again if needed) before paying.',
      }
    }

    const {error: stripeError, paymentMethod} = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (stripeError) {
      logDataByLevel({
        level: 'error',
        message: stripeError.message ?? 'Unknown Stripe error',
        data: stripeError.code,
      })
      return {success: false, message: stripeError.message}
    }

    // If the UI already timed out during tokenization, do NOT initiate the
    // charge — the user has been told it timed out and would otherwise be
    // charged after the fact.
    if (signal.aborted) return {success: false, message: 'Payment cancelled'}

    const checkoutBody = buildCheckoutBodyWithStripePaymentMethodId(paymentMethod?.id)
    if (!checkoutBody) {
      return {success: false, message: 'The payload is incomplete or missing'}
    }
    // Pass the signal so the checkout fetch is aborted if the timeout fires.
    const stripePayment = await checkOutWithStripe(checkoutBody, signal)
    return {
      success: Boolean(stripePayment?.success),
      message: (stripePayment as {message?: string} | null)?.message,
    }
  }

  const handlePaymentButtonClick = async (syntheticBaseEvent: React.MouseEvent<HTMLButtonElement>) => {
    syntheticBaseEvent.preventDefault()

    const decision = shouldAcceptPaymentClick({
      lastAcceptedTimestamp: lastAcceptedTimestampRef.current,
      eventTimestamp: syntheticBaseEvent.timeStamp,
      debounceMs: 3000,
      inFlight: inFlightRef.current,
      stripeReady,
    })
    if (!decision.accept) return

    lastAcceptedTimestampRef.current = syntheticBaseEvent.timeStamp
    inFlightRef.current = true
    try {
      await handlePaymentAttempt({
        createPayment: createStripePayment,
        setProcessing: sendPaymentIsProcessing,
        clearCard: () => ref?.clear(),
        castSubmission: () => {
          const submissionObject: SubmissionObject = {
            photoImage: stripePayload.submissionUrl,
            photoName: stripePayload.submissionName,
            additionalFormFields: _.map(stripePayload.additionalFormFields, formField => ({
              id: formField.id,
              value: formField.value,
              field_label: formField.name,
            })),
          }
          return onCastSubmission(submissionObject)
        },
        advance: onNext,
        notifyUser: message => toast.error(message),
        logError: (message, data) => logDataByLevel({level: 'error', message, data}),
        timeoutMs: CHECKOUT_TIMEOUT_MS,
      })
    } finally {
      inFlightRef.current = false
    }
  }

  const CARD_ELEMENT_OPTIONS = {
    iconStyle: 'solid' as const,

    style: {
      base: {
        'iconColor': '#4a90e2',
        'color': '#111',
        'fontSize': '16px',
        'fontFamily': 'BlinkMacSystemFont, -apple-system, Roboto, Segoe UI, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, -apple-system, BlinkMacSystemFont, sans-serif',
        'fontSmoothing': 'antialiased',
        '::placeholder': {color: '#7a7978'},
      },
      invalid: {color: '#d9534f'},
    },
  }

  return (
    <div className={styles['checkout-form']}>
      <h3 className='subtitle is-4 has-text-weight-bold mb-6'>
        Thank you! Please complete your photo upload by submitting the entry fee below.
      </h3>
      <CardElement options={CARD_ELEMENT_OPTIONS} onReady={(e: StripeCardElement) => setRef(e)} />
      <div className='buttons is-right mt-6'>
        <button
          onClick={handlePaymentButtonClick}
          className='button is-large is-dark'
          disabled={isDisabled || !stripeReady}
        >
          {paymentButtonText}
        </button>
      </div>
    </div>
  )
}

export default CheckoutForm
